<template>
  <form class="space-y-4" @submit="handleSaveInsuranceCase">
    <div v-if="booking.insuranceCase?.insuranceProvider === 'axa'" class="space-y-4">
      <h2>{{ t('axaSettings') }}</h2>
      <AxaInsuranceDataForm handover-view />
    </div>
    <div v-if="booking.insuranceCase?.insuranceProvider === 'roland'" class="space-y-4">
      <h2>{{ t('rolandSettings') }}</h2>
      <RolandInsuranceDataForm handover-view />
    </div>
    <div v-if="booking.insuranceCase?.insuranceProvider === 'europ'" class="space-y-4">
      <h2>{{ t('europSettings') }}</h2>
      <EuropInsuranceDataForm handover-view />
    </div>
    <div v-if="booking.insuranceCase?.insuranceProvider === 'volvo'" class="space-y-4">
      <h2>{{ t('volvoSettings') }}</h2>
      <VolvoInsuranceDataForm handover-view />
    </div>
    <div v-if="booking.insuranceCase?.insuranceProvider === 'jlr'" class="space-y-4">
      <h2>{{ t('jlrSettings') }}</h2>
      <JlrInsuranceDataForm handover-view />
    </div>
    <div v-if="formMeta.dirty" class="mt-4 flex justify-end gap-4">
      <CVButton variant="default" :is-loading="isSaving">{{ t('save') }}</CVButton>
      <CVButton variant="secondary" :disabled="isSaving" @click.prevent="resetForm()">{{
        t('cancel')
      }}</CVButton>
    </div>
  </form>
</template>
<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { useUpdateInsuranceCase } from '@/queries/use-insurance-cases';
import { computed, ref, watchEffect } from 'vue';
import {
  type AxaInsuranceData,
  type CreateInsuranceCaseDto,
  type RolandInsuranceData,
  type EuropInsuranceData,
  type UpdateInsuranceCaseDto,
} from '@carvia/ros-client-types';
import { useForm } from 'vee-validate';
import { insuranceCaseHandoverSchema } from '@/validation/insurance-case.schema';
import AxaInsuranceDataForm from '@/views/insurance-cases/insurance-case/AxaInsuranceDataForm.vue';
import RolandInsuranceDataForm from '@/views/insurance-cases/insurance-case/RolandInsuranceDataForm.vue';
import EuropInsuranceDataForm from '@/views/insurance-cases/insurance-case/EuropInsuranceDataForm.vue';
import VolvoInsuranceDataForm from '@/views/insurance-cases/insurance-case/VolvoInsuranceDataForm.vue';
import JlrInsuranceDataForm from '@/views/insurance-cases/insurance-case/JlrInsuranceDataForm.vue';

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'valid', value: boolean): void;
}>();

const { t } = useI18n();

const { isPending: isSaving, mutateAsync: updateInsuranceCase } = useUpdateInsuranceCase();

const initialValues = ref<UpdateInsuranceCaseDto>(props.booking.insuranceCase as any);

const savedValue = ref<UpdateInsuranceCaseDto>(props.booking.insuranceCase as any);

const {
  handleSubmit,
  values: formValues,
  meta: formMeta,
  resetForm,
} = useForm<UpdateInsuranceCaseDto>({
  initialValues: initialValues.value,
  validationSchema: insuranceCaseHandoverSchema,
});

const isValid = computed(() => {
  switch (savedValue.value.insuranceProvider) {
    case 'axa': {
      const insuranceData = savedValue.value.insuranceData as AxaInsuranceData;
      return !!insuranceData.licencePlate && !!insuranceData.porscheCenter;
    }
    case 'roland': {
      const insuranceData = savedValue.value.insuranceData as RolandInsuranceData;
      return !!insuranceData.licencePlate;
    }
    case 'europ': {
      const insuranceData = savedValue.value.insuranceData as EuropInsuranceData;
      return !!insuranceData.licencePlate && !!insuranceData.porscheCenter;
    }
    default:
      return true;
  }
});

watchEffect(() => {
  if (formMeta.value.dirty) {
    emit('valid', false);
    return;
  }
  emit('valid', isValid.value);
});

const handleSaveInsuranceCase = handleSubmit(
  async (values: CreateInsuranceCaseDto | UpdateInsuranceCaseDto) => {
    const res = await updateInsuranceCase({
      id: props.booking.insuranceCase!.id,
      insuranceCase: values as UpdateInsuranceCaseDto,
    });
    resetForm({ values: res as any });
    savedValue.value = values as UpdateInsuranceCaseDto;
  },
);
</script>
<i18n lang="json">
{
  "en": {
    "axaSettings": "AXA Settings",
    "rolandSettings": "Roland Settings",
    "europSettings": "Europ Settings",
    "volvoSettings": "Volvo Settings",
    "jlrSettings": "JLR Settings"
  },
  "de": {
    "axaSettings": "AXA-Einstellungen",
    "rolandSettings": "Roland-Einstellungen",
    "europSettings": "Europ-Einstellungen",
    "volvoSettings": "Volvo-Einstellungen",
    "jlrSettings": "JLR-Einstellungen"
  },
  "es": {
    "axaSettings": "Ajustes AXA",
    "rolandSettings": "Ajustes Roland",
    "europSettings": "Ajustes Europ",
    "volvoSettings": "Ajustes Volvo",
    "jlrSettings": "Ajustes JLR"
  }
}
</i18n>
